/**
 * 设置页面
 * 用户信息编辑、密码修改、偏好设置
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Layout, Card, Button, Form, Input, message, Typography,
  Tabs, Space, Avatar, Divider, Switch, Select
} from 'antd'
import {
  ArrowLeftOutlined, UserOutlined, LockOutlined, SettingOutlined,
  MailOutlined, SaveOutlined
} from '@ant-design/icons'
import { useAuthStore } from '../store/authStore'
import { api } from '../services/api'
import './Settings.css'

const { Header, Content } = Layout
const { Title, Text } = Typography
const { TabPane } = Tabs

const Settings: React.FC = () => {
  const navigate = useNavigate()
  const { user, updateUser } = useAuthStore()
  const [profileForm] = Form.useForm()
  const [passwordForm] = Form.useForm()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        username: user.username,
        email: user.email
      })
    }
  }, [user, profileForm])

  const handleProfileUpdate = async (values: any) => {
    try {
      setLoading(true)
      const response = await api.users.updateProfile({
        username: values.username,
        email: values.email
      })

      if (response.success) {
        message.success('个人信息更新成功')
        updateUser(response.data)
      }
    } catch (error: any) {
      console.error('更新个人信息失败:', error)
      message.error('更新失败')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async (values: any) => {
    try {
      setLoading(true)
      const response = await api.users.changePassword({
        old_password: values.old_password,
        new_password: values.new_password
      })

      if (response.success) {
        message.success('密码修改成功')
        passwordForm.resetFields()
      }
    } catch (error: any) {
      console.error('修改密码失败:', error)
      message.error('修改失败')
    } finally {
      setLoading(false)
    }
  }

  const validatePassword = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('请输入密码'))
    }
    if (value.length < 6) {
      return Promise.reject(new Error('密码长度至少6个字符'))
    }
    return Promise.resolve()
  }

  const validateConfirmPassword = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('请确认密码'))
    }
    if (value !== passwordForm.getFieldValue('new_password')) {
      return Promise.reject(new Error('两次输入的密码不一致'))
    }
    return Promise.resolve()
  }

  return (
    <Layout className="settings-layout">
      <Header className="settings-header">
        <div className="header-left">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            type="text"
            style={{ color: 'white' }}
          >
            返回
          </Button>
          <Title level={3} style={{ color: 'white', margin: 0 }}>
            <SettingOutlined /> 设置
          </Title>
        </div>
      </Header>

      <Content className="settings-content">
        <Card className="settings-card">
          <Tabs defaultActiveKey="profile" tabPosition="left">
            {/* 个人信息 */}
            <TabPane
              tab={
                <span>
                  <UserOutlined />
                  个人信息
                </span>
              }
              key="profile"
            >
              <div className="settings-section">
                <div className="section-header">
                  <Title level={4}>个人信息</Title>
                  <Text type="secondary">管理你的个人资料</Text>
                </div>

                <Divider />

                <div className="profile-avatar">
                  <Avatar size={80} style={{ backgroundColor: 'var(--info)' }}>
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                  <div style={{ marginLeft: 16 }}>
                    <Title level={5}>{user?.username}</Title>
                    <Text type="secondary">{user?.email}</Text>
                  </div>
                </div>

                <Divider />

                <Form
                  form={profileForm}
                  layout="vertical"
                  onFinish={handleProfileUpdate}
                  style={{ maxWidth: 500 }}
                >
                  <Form.Item
                    name="username"
                    label="用户名"
                    rules={[
                      { required: true, message: '请输入用户名' },
                      { min: 3, message: '用户名至少3个字符' },
                      { max: 20, message: '用户名最多20个字符' }
                    ]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="用户名" />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label="邮箱"
                    rules={[
                      { required: true, message: '请输入邮箱' },
                      { type: 'email', message: '请输入有效的邮箱地址' }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="邮箱地址" />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      icon={<SaveOutlined />}
                    >
                      保存更改
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            </TabPane>

            {/* 安全设置 */}
            <TabPane
              tab={
                <span>
                  <LockOutlined />
                  安全设置
                </span>
              }
              key="security"
            >
              <div className="settings-section">
                <div className="section-header">
                  <Title level={4}>修改密码</Title>
                  <Text type="secondary">定期修改密码以保护账号安全</Text>
                </div>

                <Divider />

                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handlePasswordChange}
                  style={{ maxWidth: 500 }}
                >
                  <Form.Item
                    name="old_password"
                    label="当前密码"
                    rules={[{ required: true, message: '请输入当前密码' }]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="输入当前密码"
                    />
                  </Form.Item>

                  <Form.Item
                    name="new_password"
                    label="新密码"
                    rules={[{ validator: validatePassword }]}
                    hasFeedback
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="输入新密码（至少6个字符）"
                    />
                  </Form.Item>

                  <Form.Item
                    name="confirm_password"
                    label="确认新密码"
                    dependencies={['new_password']}
                    rules={[{ validator: validateConfirmPassword }]}
                    hasFeedback
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="再次输入新密码"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      icon={<SaveOutlined />}
                    >
                      修改密码
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            </TabPane>

            {/* 偏好设置 */}
            <TabPane
              tab={
                <span>
                  <SettingOutlined />
                  偏好设置
                </span>
              }
              key="preferences"
            >
              <div className="settings-section">
                <div className="section-header">
                  <Title level={4}>偏好设置</Title>
                  <Text type="secondary">自定义你的使用体验</Text>
                </div>

                <Divider />

                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div className="preference-item">
                    <div>
                      <Text strong>默认搜索类型</Text>
                      <div>
                        <Text type="secondary">选择默认的搜索方式</Text>
                      </div>
                    </div>
                    <Select
                      defaultValue="keyword"
                      style={{ width: 200 }}
                      options={[
                        { value: 'keyword', label: '关键词搜索' },
                        { value: 'semantic', label: '语义搜索', disabled: true }
                      ]}
                    />
                  </div>

                  <Divider />

                  <div className="preference-item">
                    <div>
                      <Text strong>默认视图模式</Text>
                      <div>
                        <Text type="secondary">选择文章列表的默认显示方式</Text>
                      </div>
                    </div>
                    <Select
                      defaultValue="list"
                      style={{ width: 200 }}
                      options={[
                        { value: 'list', label: '列表视图' },
                        { value: 'card', label: '卡片视图' }
                      ]}
                    />
                  </div>

                  <Divider />

                  <div className="preference-item">
                    <div>
                      <Text strong>自动保存收藏</Text>
                      <div>
                        <Text type="secondary">使用Chrome插件时自动保存收藏</Text>
                      </div>
                    </div>
                    <Switch defaultChecked />
                  </div>

                  <Divider />

                  <div className="preference-item">
                    <div>
                      <Text strong>邮件通知</Text>
                      <div>
                        <Text type="secondary">接收重要更新和通知邮件</Text>
                      </div>
                    </div>
                    <Switch defaultChecked />
                  </div>

                  <Divider />

                  <div className="preference-item">
                    <div>
                      <Text strong>数据导出</Text>
                      <div>
                        <Text type="secondary">导出你的所有收藏数据</Text>
                      </div>
                    </div>
                    <Button disabled>导出数据（即将上线）</Button>
                  </div>

                  <Divider />

                  <div className="preference-item">
                    <div>
                      <Text strong>数据导入</Text>
                      <div>
                        <Text type="secondary">从其他平台导入收藏数据</Text>
                      </div>
                    </div>
                    <Button disabled>导入数据（即将上线）</Button>
                  </div>
                </Space>
              </div>
            </TabPane>
          </Tabs>
        </Card>
      </Content>
    </Layout>
  )
}

export default Settings

